import { renameSheetReferencesInFormula } from '@auw/xlsx-engine/structural-shift';
import type { CfBlock } from '@auw/xlsx-engine/conditional-format';

export interface SheetRenameTransition {
  from: string;
  to: string;
}

interface FormulaValidation {
  formula1?: string;
  formula2?: string;
}

interface NativeFormulaCell {
  f?: unknown;
}

interface NativeFormulaWorkbook {
  Sheets: Record<string, Record<string, unknown>>;
}

/** Apply renderer-observed renames in chronological order to parser-era metadata. */
export const rewriteFormulaThroughSheetRenames = (
  formula: string,
  transitions: readonly SheetRenameTransition[],
): string => transitions.reduce(
  (current, transition) => renameSheetReferencesInFormula(
    current,
    transition.from,
    transition.to,
  ),
  formula,
);

export const rewriteValidationSheetReferences = <T extends FormulaValidation>(
  validations: readonly T[],
  transitions: readonly SheetRenameTransition[],
): T[] => validations.map((validation) => ({
  ...validation,
  ...(validation.formula1 !== undefined
    ? { formula1: rewriteFormulaThroughSheetRenames(validation.formula1, transitions) }
    : {}),
  ...(validation.formula2 !== undefined
    ? { formula2: rewriteFormulaThroughSheetRenames(validation.formula2, transitions) }
    : {}),
}));

export const rewriteConditionalFormatSheetReferences = (
  blocks: readonly CfBlock[],
  transitions: readonly SheetRenameTransition[],
): CfBlock[] => blocks.map((block) => ({
  ...block,
  rects: block.rects.map((rect) => ({ ...rect })),
  rules: block.rules.map((rule) => {
    const sourceRule = rule.sourceRule
      ? JSON.parse(JSON.stringify(rule.sourceRule)) as NonNullable<typeof rule.sourceRule>
      : undefined;
    if (sourceRule) {
      for (const field of ['formula', 'formulas'] as const) {
        const value = sourceRule[field];
        if (typeof value === 'string') {
          sourceRule[field] = rewriteFormulaThroughSheetRenames(value, transitions);
        } else if (Array.isArray(value)) {
          sourceRule[field] = value.map((formula) =>
            typeof formula === 'string'
              ? rewriteFormulaThroughSheetRenames(formula, transitions)
              : formula);
        }
      }
    }
    return {
      ...rule,
      formulas: rule.formulas.map((formula) =>
        rewriteFormulaThroughSheetRenames(formula, transitions)),
      ...(sourceRule ? { sourceRule } : {}),
    };
  }),
}));

/** Mutate only native SheetJS formula fields; values and package metadata stay intact. */
export const rewriteNativeWorkbookSheetReferences = (
  workbook: NativeFormulaWorkbook,
  transition: SheetRenameTransition,
): void => {
  for (const sheet of Object.values(workbook.Sheets)) {
    for (const [address, candidate] of Object.entries(sheet)) {
      if (address.startsWith('!') || !candidate || typeof candidate !== 'object') continue;
      const cell = candidate as NativeFormulaCell;
      if (typeof cell.f !== 'string' || !cell.f) continue;
      cell.f = renameSheetReferencesInFormula(cell.f, transition.from, transition.to);
    }
  }
};

/**
 * A session rename preserves sheet count/order and changes exactly one tab name.
 * Insert/delete/reorder frames deliberately do not masquerade as rename events.
 */
export const inferSheetRenameTransition = (
  previousNames: readonly string[],
  nextNames: readonly string[],
): SheetRenameTransition | null => {
  if (previousNames.length !== nextNames.length) return null;
  const changed: number[] = [];
  for (let index = 0; index < previousNames.length; index += 1) {
    if (previousNames[index] !== nextNames[index]) changed.push(index);
  }
  if (changed.length !== 1) return null;
  const index = changed[0];
  if (nextNames.includes(previousNames[index]) || previousNames.includes(nextNames[index])) {
    return null;
  }
  return { from: previousNames[index], to: nextNames[index] };
};
